import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_FontGenerator = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/font-generator',
			name: 'Font Generator — Style Text for Bios, Posts & Social Profiles by Lift',
			description:
				'Type any text and see it styled across hundreds of fonts — perfect for Instagram bios, TikTok usernames, post captions, and more. Copy the style you want. Try Lift now!',
			url: 'https://liftapp.ai/font-generator',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/font-generator#product' },
				{ '@id': 'https://liftapp.ai/font-generator#howto' },
				{ '@id': 'https://liftapp.ai/font-generator#benefits' },
				{ '@id': 'https://liftapp.ai/font-generator#faq' },
				{ '@id': 'https://liftapp.ai/font-generator#why-lift' },
				{ '@id': 'https://liftapp.ai/font-generator#explore-tools' },
				{ '@id': 'https://liftapp.ai/font-generator#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/font-generator#product',
			name: 'Font Generator',
			description:
				'Style your bio, username, or post headline in seconds. Type your text, browse hundreds of font styles in real time, and copy the one that fits.',
			url: 'https://liftapp.ai/font-generator',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/font-generator#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Type your text',
					text: 'Enter a bio intro, username, caption, or headline — the font grid updates live across all available styles',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Browse by category',
					text: 'Switch tabs to filter by vibe — elegant scripts, bold display, decorative, and more',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Copy and paste anywhere',
					text: 'Click Copy and paste the styled text straight into Instagram, TikTok, Twitter, or any other platform',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/font-generator#benefits',
			name: 'Who can benefit from the Font Generator?',
			description:
				'Lift’s Font Generator is useful for creators, social media users, designers, and business owners who want stylish, on-brand text for bios, usernames, captions, and posts.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Instagram users can match the exact aesthetic of their account with a stylish bio or profile name in seconds',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'TikTok and short-video creators can pick eye-catching usernames and captions that stand out in a scrolling feed',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Content creators can lock in a consistent, on-brand tone for thumbnails, story overlays, and post headers',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Graphic designers can evaluate dozens of typeface options at once, speeding up layout decisions',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Small business owners can choose a font for a promo caption or profile bio without hiring a designer',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Font Generator by Lift',
				description:
					'Example of how Lift Font Generator styles text across hundreds of fonts for bios, posts, and social profiles.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/font-generator#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift Font Generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'The Lift Font Generator is a tool that shows your text rendered in every available font style simultaneously. Type a bio, username, or any text and the full grid updates in real time, organized by category.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does the Lift Font Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Type any text and the font grid updates instantly across all available styles. Browse by category tab to filter the look, then copy the style you want.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I use the copied fonts on Instagram, TikTok, or other platforms?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. The Font Generator produces Unicode-based text characters that paste directly into social media bios, usernames, captions, and story text fields. No extra app or plugin is needed on the destination platform.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Absolutely. All images generated or edited in Lift can be exported without watermark when you’re on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/font-generator#why-lift',
			name: 'Why Lift’s Font Generator stands out',
			description:
				'Lift’s Font Generator combines high-quality results, intelligent rendering, cross-platform support, and affordability — making it a trusted choice for millions of users worldwide.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Why use Lift Font Generator',
				description:
					'Comparison of Lift’s Font Generator benefits — high-quality results, intelligent rendering, multi-platform access, and cost savings.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/font-generator#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/font-generator#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Font Generator',
					item: 'https://liftapp.ai/font-generator',
				},
			],
		},
	],
};
